import React, { useState, useEffect } from 'react';
import { X, TrendingUp, MousePointerClick, DollarSign, Percent, BarChart3 } from 'lucide-react';
import Button from '../../../components/ui/Button';
import dealService from '../../../services/dealService';

const DealMetricsModal = ({ deal, onClose }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMetrics();
  }, [deal]);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      const data = await dealService?.getDealMetrics(deal?.id);
      setMetrics(data);
    } catch (error) {
      console.error('Error loading metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const priceHistory = deal?.priceHistory || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Deal Performance Metrics</h2>
            <p className="text-sm text-gray-600 mt-1">{deal?.title}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <>
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-blue-900">Total Clicks</p>
                    <MousePointerClick className="w-5 h-5 text-blue-600" />
                  </div>
                  <p className="text-2xl font-bold text-blue-900">
                    {metrics?.clickCount?.toLocaleString() || 0}
                  </p>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-green-900">Conversions</p>
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <p className="text-2xl font-bold text-green-900">
                    {metrics?.conversionCount?.toLocaleString() || 0}
                  </p>
                </div>

                <div className="bg-indigo-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-indigo-900">Conversion Rate</p>
                    <Percent className="w-5 h-5 text-indigo-600" />
                  </div>
                  <p className="text-2xl font-bold text-indigo-900">
                    {metrics?.conversionRate || 0}%
                  </p>
                </div>

                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-purple-900">Total Revenue</p>
                    <DollarSign className="w-5 h-5 text-purple-600" />
                  </div>
                  <p className="text-2xl font-bold text-purple-900">
                    ${metrics?.revenueGenerated?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                  </p>
                </div>
              </div>

              {/* Additional Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Average Order Value</p>
                  <p className="text-xl font-bold text-gray-900">
                    ${metrics?.averageOrderValue || '0.00'}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Deal Status</p>
                  <p className="text-xl font-bold text-gray-900 capitalize">
                    {deal?.status}
                  </p>
                </div>
              </div>

              {/* Price History */}
              {priceHistory?.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-gray-700" />
                    <h3 className="text-lg font-semibold text-gray-900">Price History</h3>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="space-y-3">
                      {priceHistory?.slice()?.reverse()?.map((entry, index) => (
                        <div key={index} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              ${entry?.price?.toFixed(2)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(entry?.timestamp)?.toLocaleString()}
                            </p>
                          </div>
                          <div className="text-right">
                            {entry?.previousPrice && (
                              <p className="text-xs text-gray-500 line-through">
                                ${entry?.previousPrice?.toFixed(2)}
                              </p>
                            )}
                            <p className="text-xs text-gray-600 capitalize">
                              {entry?.source || 'manual'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Deal Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Deal Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Product</p>
                    <p className="text-sm font-medium text-gray-900">
                      {deal?.products?.brand} {deal?.products?.name}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Affiliate Partner</p>
                    <p className="text-sm font-medium text-gray-900">
                      {deal?.affiliatePartner || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Discount</p>
                    <p className="text-sm font-medium text-gray-900">
                      {deal?.discountValue}{deal?.discountType === 'percentage' ? '%' : '$'} off
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Expiration Date</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(deal?.expirationDate)?.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DealMetricsModal;